import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError, authApi } from '../lib/api';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { AlertCircle, ArrowRight, Mail, KeyRound, ArrowLeft, Eye, EyeOff, RefreshCw } from 'lucide-react';

const AuthPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/';
  const { login, register, verifyEmail, resendCode } = useAuth();
  
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerCompany, setRegisterCompany] = useState('');
  const [registerPhone, setRegisterPhone] = useState('');
  
  const [showVerification, setShowVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [resending, setResending] = useState(false);

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [showResetCode, setShowResetCode] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resentInfo, setResentInfo] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(loginEmail, loginPassword, rememberMe);
      navigate(redirect);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await register({
        name: registerName,
        email: registerEmail,
        password: registerPassword,
        company: registerCompany || null,
        phone: registerPhone || null
      });
      setVerificationEmail(registerEmail);
      setShowVerification(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (verificationCode.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      await verifyEmail(verificationEmail, verificationCode);
      navigate(redirect);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    setError('');
    try {
      await resendCode(verificationEmail);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setResending(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await authApi.forgotPassword(forgotEmail);
      setShowResetCode(true);
      setResendCooldown(30);
    } catch (err) {
      setError(err.message || t('auth.code_invalid'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendResetCode = async () => {
    if (resendCooldown > 0) return;
    setResentInfo('');
    setError('');
    try {
      await authApi.forgotPassword(forgotEmail);
      setResentInfo(t('auth.resent_ok', 'Code renvoyé. Vérifiez votre boîte email.'));
      setResendCooldown(30);
    } catch (err) {
      setError(err.message || t('auth.code_invalid'));
    }
  };

  // Cooldown countdown for resend button
  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const passwordStrength = (pwd) => {
    if (!pwd) return { level: 0, label: '' };
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^\w\s]/.test(pwd)) score++;
    const levels = [
      { level: 1, label: t('auth.pwd_weak', 'Faible'), color: 'bg-red-500' },
      { level: 2, label: t('auth.pwd_fair', 'Moyen'), color: 'bg-orange-500' },
      { level: 3, label: t('auth.pwd_good', 'Bon'), color: 'bg-yellow-500' },
      { level: 4, label: t('auth.pwd_strong', 'Fort'), color: 'bg-green-500' },
    ];
    return levels[Math.min(score - 1, 3)] || { level: 0, label: '', color: '' };
  };

  const handleResetPassword = async () => {
    if (resetCode.length !== 6) return;
    if (newPassword.length < 6) { setError(t('auth.password_too_short')); return; }
    if (newPassword !== newPasswordConfirm) { setError(t('auth.password_mismatch')); return; }
    
    setLoading(true);
    setError('');
    try {
      await authApi.resetPassword(forgotEmail, resetCode, newPassword);
      setResetSuccess(true);
    } catch (err) {
      setError(err.message || t('auth.code_invalid'));
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    if (resetSuccess) {
      return (
        <div className="min-h-screen py-16 flex items-center justify-center" data-testid="reset-success-page">
          <div className="w-full max-w-md px-6">
            <Card className="border border-border rounded-none">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle className="text-2xl">{t('auth.reset_success_title')}</CardTitle>
                <CardDescription>{t('auth.reset_success_desc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => { setShowForgotPassword(false); setResetSuccess(false); setShowResetCode(false); setResetCode(''); setNewPassword(''); setNewPasswordConfirm(''); setError(''); }} className="w-full rounded-none" data-testid="back-to-login-btn">
                  {t('auth.back_to_login')} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    if (showResetCode) {
      return (
        <div className="min-h-screen py-16 flex items-center justify-center" data-testid="reset-code-page">
          <div className="w-full max-w-md px-6">
            <Card className="border border-border rounded-none">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-2xl">{t('auth.reset_title')}</CardTitle>
                <CardDescription>
                  {t('auth.reset_desc')} <strong>{forgotEmail}</strong>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={resetCode} onChange={setResetCode} data-testid="reset-code-input">
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <div className="space-y-2">
                  <Label>{t('auth.new_password')}</Label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={t('auth.min_chars')}
                      className="rounded-none pr-10"
                      data-testid="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      data-testid="toggle-new-password-visibility"
                      tabIndex={-1}
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {newPassword && (() => {
                    const s = passwordStrength(newPassword);
                    return (
                      <div className="flex items-center gap-2" data-testid="password-strength">
                        <div className="flex-1 h-1 bg-muted overflow-hidden">
                          <div className={`h-full ${s.color || ''} transition-all`} style={{ width: `${(s.level / 4) * 100}%` }} />
                        </div>
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</span>
                      </div>
                    );
                  })()}
                </div>
                <div className="space-y-2">
                  <Label>{t('auth.confirm_password')}</Label>
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPasswordConfirm}
                    onChange={(e) => setNewPasswordConfirm(e.target.value)}
                    placeholder={t('auth.confirm_placeholder')}
                    className="rounded-none"
                    data-testid="confirm-password"
                  />
                </div>
                {resentInfo && (
                  <div className="flex items-start gap-2 p-3 bg-green-50 text-green-700 text-sm border border-green-200" data-testid="resent-info">
                    <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{resentInfo}</span>
                  </div>
                )}
                {error && (
                  <div className="flex items-start gap-2 p-3 bg-error/10 text-error text-sm">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{error}</span>
                  </div>
                )}
                <Button onClick={handleResetPassword} className="w-full rounded-none" disabled={loading || resetCode.length !== 6} data-testid="reset-password-btn">
                  {loading ? t('auth.resetting') : t('auth.reset_btn')}
                </Button>
                <button
                  type="button"
                  onClick={handleResendResetCode}
                  disabled={resendCooldown > 0}
                  className="flex items-center gap-1 text-sm text-primary hover:underline mx-auto disabled:opacity-50 disabled:no-underline"
                  data-testid="resend-reset-code-btn"
                >
                  <RefreshCw className={`w-3 h-3 ${resendCooldown > 0 ? '' : ''}`} />
                  {resendCooldown > 0
                    ? t('auth.resend_cooldown', { sec: resendCooldown, defaultValue: `Renvoyer le code (${resendCooldown}s)` })
                    : t('auth.resend_code', 'Renvoyer le code')}
                </button>
                <button onClick={() => { setShowForgotPassword(false); setShowResetCode(false); setError(''); setResentInfo(''); }} className="flex items-center gap-1 text-sm text-primary hover:underline mx-auto" data-testid="back-to-login-link">
                  <ArrowLeft className="w-3 h-3" /> {t('auth.back_to_login')}
                </button>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen py-16 flex items-center justify-center" data-testid="forgot-password-page">
        <div className="w-full max-w-md px-6">
          <Card className="border border-border rounded-none">
            <CardHeader className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-primary" />
              </div>
              <CardTitle className="text-2xl">{t('auth.forgot_title')}</CardTitle>
              <CardDescription>{t('auth.forgot_desc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">{t('auth.email')}</Label>
                  <Input id="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="email@exemple.com" required className="rounded-none" data-testid="forgot-email" />
                </div>
                {error && (
                  <div className="flex items-start gap-2 p-3 bg-error/10 text-error text-sm">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{error}</span>
                  </div>
                )}
                <Button type="submit" className="w-full rounded-none" disabled={loading} data-testid="forgot-submit">
                  {loading ? t('auth.sending') : t('auth.forgot_btn')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <button type="button" onClick={() => { setShowForgotPassword(false); setError(''); }} className="flex items-center gap-1 text-sm text-primary hover:underline mx-auto" data-testid="back-to-login-link-2">
                  <ArrowLeft className="w-3 h-3" /> {t('auth.back_to_login')}
                </button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (showVerification) {
    return (
      <div className="min-h-screen py-16 flex items-center justify-center" data-testid="verification-page">
        <div className="w-full max-w-md px-6">
          <Card className="border border-border rounded-none">
            <CardHeader className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-primary" />
              </div>
              <CardTitle className="text-2xl">{t('auth.verify_title')}</CardTitle>
              <CardDescription>
                {t('auth.verify_desc')}<br />
                <strong>{verificationEmail}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={verificationCode} onChange={setVerificationCode} data-testid="verification-code-input">
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 bg-error/10 text-error text-sm">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button onClick={handleVerifyEmail} className="w-full rounded-none" disabled={loading || verificationCode.length !== 6} data-testid="verify-btn">
                {loading ? t('auth.verifying') : t('auth.verify_btn')}
              </Button>

              <div className="text-center">
                <button onClick={handleResendCode} disabled={resending} className="text-sm text-primary hover:underline" data-testid="resend-code-btn">
                  {resending ? t('auth.resending') : t('auth.resend')}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16 flex items-center justify-center" data-testid="auth-page">
      <SEO title="Connexion" description="Connectez-vous à votre compte Puzzle Video pour suivre vos demandes de devis et gérer vos locations." noindex />
      <div className="w-full max-w-md px-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold tracking-tight mb-2">
            <span className="text-black">{t('auth.title')}</span>
            <span className="text-primary">{t('auth.title_accent')}</span>
          </h1>
          <p className="text-muted-foreground">{t('auth.subtitle')}</p>
        </div>

        <Card className="border border-border rounded-none">
          <CardContent className="pt-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 rounded-none">
                <TabsTrigger value="login" className="rounded-none" data-testid="login-tab">
                  {t('auth.login_tab')}
                </TabsTrigger>
                <TabsTrigger value="register" className="rounded-none" data-testid="register-tab">
                  {t('auth.register_tab')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="mt-6">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">{t('auth.email')}</Label>
                    <Input id="login-email" type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="email@exemple.com" required className="rounded-none" data-testid="login-email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">{t('auth.password')}</Label>
                    <Input id="login-password" type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="********" required className="rounded-none" data-testid="login-password" />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="remember-me-label">
                      <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="w-4 h-4 rounded border-gray-300 accent-primary" data-testid="remember-me-checkbox" />
                      <span className="text-sm text-muted-foreground">{t('auth.remember_me', 'Se souvenir de moi')}</span>
                    </label>
                    <button type="button" onClick={() => { setShowForgotPassword(true); setForgotEmail(loginEmail); setError(''); }} className="text-sm text-primary hover:underline" data-testid="forgot-password-link">
                      {t('auth.forgot_password')}
                    </button>
                  </div>

                  {error && activeTab === 'login' && (
                    <div className="flex items-start gap-2 p-3 bg-error/10 text-error text-sm">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <Button type="submit" className="w-full rounded-none" disabled={loading} data-testid="login-submit">
                    {loading ? t('auth.logging_in') : t('auth.login_btn')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register" className="mt-6">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">{t('auth.register_name')} *</Label>
                    <Input id="register-name" type="text" value={registerName} onChange={(e) => setRegisterName(e.target.value)} placeholder="Jean Dupont" required className="rounded-none" data-testid="register-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">{t('auth.email')} *</Label>
                    <Input id="register-email" type="email" value={registerEmail} onChange={(e) => setRegisterEmail(e.target.value)} placeholder="email@exemple.com" required className="rounded-none" data-testid="register-email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">{t('auth.password')} *</Label>
                    <Input id="register-password" type="password" value={registerPassword} onChange={(e) => setRegisterPassword(e.target.value)} placeholder="********" required minLength={6} className="rounded-none" data-testid="register-password" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-company">{t('auth.register_company')}</Label>
                    <Input id="register-company" type="text" value={registerCompany} onChange={(e) => setRegisterCompany(e.target.value)} className="rounded-none" data-testid="register-company" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-phone">{t('auth.register_phone')}</Label>
                    <Input id="register-phone" type="tel" value={registerPhone} onChange={(e) => setRegisterPhone(e.target.value)} placeholder="+33 1 23 45 67 89" className="rounded-none" data-testid="register-phone" />
                  </div>

                  {error && activeTab === 'register' && (
                    <div className="flex items-start gap-2 p-3 bg-error/10 text-error text-sm">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <Button type="submit" className="w-full rounded-none" disabled={loading} data-testid="register-submit">
                    {loading ? t('auth.registering') : t('auth.register_btn')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          {t('auth.terms')}
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
